import React from 'react';
import { createPortal } from 'react-dom';
import {
  ChevronUpIcon,
  ChevronDownIcon,
  ArrowsUpDownIcon,
  EllipsisHorizontalIcon
} from '@heroicons/react/24/outline';

const Table = ({ 
  columns = [], 
  data = [], 
  sortable = true,
  sortColumn = null,
  sortDirection = 'asc',
  onSort,
  onRowClick,
  loading = false,
  emptyMessage = "No data available",
  emptyState,
  className = '',
  size = 'md'
}) => {
  const handleSort = (columnId) => {
    if (!sortable || !onSort) return;
    
    if (sortColumn === columnId) {
      const newDirection = sortDirection === 'asc' ? 'desc' : 'asc';
      onSort(columnId, newDirection);
    } else {
      onSort(columnId, 'asc');
    }
  };

  const sizeStyles = {
    sm: 'text-xs',
    md: 'text-sm',
    lg: 'text-base'
  };

  const getSortIcon = (columnId) => {
    if (sortColumn !== columnId) {
      return <ArrowsUpDownIcon className="w-4 h-4 opacity-0 group-hover:opacity-50 transition-opacity" />;
    }
    return sortDirection === 'asc' 
      ? <ChevronUpIcon className="w-4 h-4 text-primary" />
      : <ChevronDownIcon className="w-4 h-4 text-primary" />;
  };

  if (loading) {
    return (
      <div className="bg-card rounded-lg border border-border overflow-hidden">
        <div className="animate-pulse">
          {/* Header skeleton */}
          <div className="bg-muted border-b border-border">
            <div className="grid grid-cols-12 gap-4 px-6 py-4">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="col-span-3">
                  <div className="h-4 bg-muted-foreground/20 rounded"></div>
                </div>
              ))}
            </div>
          </div>
          
          {/* Rows skeleton */}
          {[...Array(5)].map((_, i) => (
            <div key={i} className="border-b border-border">
              <div className="grid grid-cols-12 gap-4 px-6 py-4">
                {[...Array(4)].map((_, j) => (
                  <div key={j} className="col-span-3">
                    <div className="h-4 bg-muted rounded"></div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className={`bg-card rounded-lg border border-border overflow-hidden ${className}`}>
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-border">
          <thead className="bg-muted">
            <tr>
              {columns.map((column) => (
                <th
                  key={column.id}
                  className={`
                    px-6 py-4 text-left font-semibold text-card-foreground uppercase tracking-wider
                    ${sizeStyles[size]}
                    ${sortable && column.sortable !== false ? 'cursor-pointer select-none group hover:bg-accent' : ''}
                    ${column.width ? column.width : ''}
                    ${column.className || ''}
                  `}
                  onClick={() => column.sortable !== false && handleSort(column.id)}
                >
                  <div className="flex items-center justify-between">
                    <span>{column.header}</span>
                    {sortable && column.sortable !== false && (
                      <span className="ml-2">
                        {getSortIcon(column.id)}
                      </span>
                    )}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          
          <tbody className="bg-card divide-y divide-border">
            {data.length === 0 ? (
              <tr>
                <td colSpan={columns.length} className="px-6 py-12 text-center">
                  {emptyState ? (
                    <div className="flex flex-col items-center">
                      {emptyState.icon && <div className="mb-4">{emptyState.icon}</div>}
                      {emptyState.title && <h3 className="text-base font-medium text-card-foreground mb-2">{emptyState.title}</h3>}
                      {emptyState.description && <p className="text-sm text-muted-foreground mb-4">{emptyState.description}</p>}
                      {emptyState.action && <div>{emptyState.action}</div>}
                    </div>
                  ) : (
                    <div className="text-muted-foreground">
                      <div className="w-12 h-12 mx-auto mb-4 bg-muted rounded-full flex items-center justify-center">
                        <EllipsisHorizontalIcon className="w-6 h-6 text-muted-foreground" />
                      </div>
                      <p className="text-sm font-medium">{emptyMessage}</p>
                    </div>
                  )}
                </td>
              </tr>
            ) : (
              data.map((row, index) => (
                <tr
                  key={row.id || index}
                  className={`
                    transition-colors duration-150
                    ${onRowClick ? 'cursor-pointer hover:bg-accent' : ''}
                    ${row.className || ''}
                  `}
                  onClick={() => onRowClick && onRowClick(row)}
                >
                  {columns.map((column) => (
                    <td
                      key={column.id}
                      className={`
                        px-6 py-4 whitespace-nowrap
                        ${sizeStyles[size]}
                        ${column.cellClassName || ''}
                      `}
                    >
                      {(() => {
                        if (column.render) {
                          return column.render(row[column.id], row, index);
                        } else if (column.cell) {
                          return column.cell(row, row[column.id], index);
                        } else {
                          return row[column.id];
                        }
                      })()}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// Status Badge Component for table cells
export const StatusBadge = ({ status, variant = 'default', color, className = '', children }) => {
  // Support both 'variant' and 'color' props for backward compatibility
  const colorToUse = color || variant;
  
  const variants = {
    success: 'bg-emerald-100 text-emerald-800 border-emerald-200',
    warning: 'bg-amber-100 text-amber-800 border-amber-200',
    error: 'bg-red-100 text-red-800 border-red-200',
    info: 'bg-blue-100 text-blue-800 border-blue-200',
    default: 'bg-muted text-muted-foreground border-border',
    gray: 'bg-muted text-muted-foreground border-border',
    orange: 'bg-orange-100 text-orange-800 border-orange-200',
    primary: 'bg-orange-100 text-orange-800 border-orange-200',
    secondary: 'bg-blue-100 text-blue-800 border-blue-200'
  };

  return (
    <span className={`
      inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border
      ${variants[colorToUse] || variants.default}
      ${className}
    `}>
      {children || status}
    </span>
  );
};

// Action Menu Component for table cells
export const ActionMenu = ({ actions = [], items = [], row, disabled = false }) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const [menuPosition, setMenuPosition] = React.useState({ top: 0, left: 0, position: 'bottom' });
  const menuRef = React.useRef(null);
  const buttonRef = React.useRef(null);

  // Support both 'actions' and 'items' props for backward compatibility
  const menuItems = items.length > 0 ? items : actions;

  React.useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    const handleScroll = () => {
      if (isOpen) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    window.addEventListener('scroll', handleScroll, true);
    window.addEventListener('resize', handleScroll);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('scroll', handleScroll, true);
      window.removeEventListener('resize', handleScroll);
    };
  }, [isOpen]);

  // Calculate dropdown position when opening
  React.useEffect(() => {
    if (isOpen && buttonRef.current) {
      const buttonRect = buttonRef.current.getBoundingClientRect();
      const windowHeight = window.innerHeight;
      const spaceBelow = windowHeight - buttonRect.bottom;
      const menuHeight = 300; // Approximate menu height
      const menuWidth = 192; // 12rem (w-48)

      let top, left, position;

      // Determine vertical position
      if (spaceBelow < menuHeight && buttonRect.top > menuHeight) {
        // Show above
        top = buttonRect.top - 8; // 8px gap
        position = 'top';
      } else {
        // Show below
        top = buttonRect.bottom + 8; // 8px gap
        position = 'bottom';
      }

      // Align to right of button
      left = buttonRect.right - menuWidth;

      // Make sure menu doesn't go off-screen on the left
      if (left < 8) {
        left = 8;
      }

      setMenuPosition({ top, left, position });
    }
  }, [isOpen]);

  if (disabled || menuItems.length === 0) {
    return null;
  }

  const handleItemClick = (item, e) => {
    e.stopPropagation();
    
    if (item.confirm && item.confirmMessage) {
      if (window.confirm(item.confirmMessage)) {
        item.onClick(row);
      }
    } else {
      item.onClick(row);
    }
    setIsOpen(false);
  };

  const renderDropdown = () => {
    if (!isOpen) return null;

    const dropdown = (
      <div
        ref={menuRef}
        className="w-48 bg-popover rounded-md shadow-xl border border-border"
        style={{
          position: 'fixed',
          top: menuPosition.position === 'top' ? 'auto' : `${menuPosition.top}px`,
          bottom: menuPosition.position === 'top' ? `${window.innerHeight - menuPosition.top}px` : 'auto',
          left: `${menuPosition.left}px`,
          zIndex: 9999,
        }}
      >
        <div className="py-1">
          {menuItems.map((item, index) => {
            if (item.type === 'divider') {
              return <div key={index} className="border-t border-gray-100 my-1" />;
            }

            return (
              <button
                key={index}
                onClick={(e) => handleItemClick(item, e)}
                disabled={item.disabled}
                className={`
                  w-full text-left px-4 py-2 text-sm transition-colors flex items-center
                  ${item.variant === 'danger' || item.className?.includes('text-red')
                    ? 'text-red-700 hover:bg-red-50'
                    : 'text-popover-foreground hover:bg-accent'
                  }
                  ${item.disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
                  ${item.className || ''}
                `}
              >
                {item.icon && (
                  <span className="mr-3 flex-shrink-0 w-4 h-4">
                    {React.cloneElement(item.icon, { className: 'w-4 h-4' })}
                  </span>
                )}
                <span>{item.label}</span>
              </button>
            );
          })}
        </div>
      </div>
    );

    return createPortal(dropdown, document.body);
  };

  return (
    <>
      <button
        ref={buttonRef}
        onClick={(e) => {
          e.stopPropagation();
          setIsOpen(!isOpen);
        }}
        className="inline-flex items-center justify-center w-8 h-8 rounded-full text-muted-foreground hover:text-card-foreground hover:bg-accent transition-colors"
      >
        <EllipsisHorizontalIcon className="w-4 h-4" />
      </button>

      {renderDropdown()}
    </>
  );
};

// Pagination Component
export const TablePagination = ({
  currentPage,
  totalPages,
  pageSize,
  totalItems,
  onPageChange,
  onPageSizeChange,
  className = ''
}) => {
  const startItem = (currentPage - 1) * pageSize + 1;
  const endItem = Math.min(currentPage * pageSize, totalItems);

  const getPageNumbers = () => {
    const pages = [];
    const maxVisible = 7;
    
    if (totalPages <= maxVisible) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      if (currentPage <= 4) {
        for (let i = 1; i <= 5; i++) pages.push(i);
        pages.push('...');
        pages.push(totalPages);
      } else if (currentPage >= totalPages - 3) {
        pages.push(1);
        pages.push('...');
        for (let i = totalPages - 4; i <= totalPages; i++) pages.push(i);
      } else {
        pages.push(1);
        pages.push('...');
        for (let i = currentPage - 1; i <= currentPage + 1; i++) pages.push(i);
        pages.push('...');
        pages.push(totalPages);
      }
    }
    
    return pages;
  };

  return (
    <div className={`flex items-center justify-between px-6 py-4 bg-card border-t border-border ${className}`}>
      <div className="flex items-center space-x-4">
        <span className="text-sm text-card-foreground">
          Showing {startItem} to {endItem} of {totalItems} results
        </span>
        
        <select
          value={pageSize}
          onChange={(e) => onPageSizeChange(Number(e.target.value))}
          className="text-sm border border-gray-300 rounded-md px-3 py-1 focus:ring-orange-500 focus:border-orange-500"
        >
          <option value={10}>10 per page</option>
          <option value={25}>25 per page</option>
          <option value={50}>50 per page</option>
          <option value={100}>100 per page</option>
        </select>
      </div>
      
      <div className="flex items-center space-x-1">
        <button
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1}
          className="px-3 py-2 text-sm font-medium text-muted-foreground bg-background border border-input rounded-l-md hover:bg-accent disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Previous
        </button>
        
        {getPageNumbers().map((page, index) => (
          page === '...' ? (
            <span key={index} className="px-3 py-2 text-sm text-muted-foreground">...</span>
          ) : (
            <button
              key={index}
              onClick={() => onPageChange(page)}
              className={`
                px-3 py-2 text-sm font-medium border
                ${currentPage === page
                  ? 'bg-orange-500 text-white border-orange-500'
                  : 'text-muted-foreground bg-background border-input hover:bg-accent'
                }
              `}
            >
              {page}
            </button>
          )
        ))}
        
        <button
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
          className="px-3 py-2 text-sm font-medium text-muted-foreground bg-background border border-input rounded-r-md hover:bg-accent disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Next
        </button>
      </div>
    </div>
  );
};

export default Table;